/**
 * TeamIntelTooltip — hover tooltip showing FIFA ranking + recent form.
 * Only fires the API call for WC teams (checked via isWcTeam registry).
 * Used in both WorldCupDisplayPage and FootballHubPage.
 */

import { useState } from 'react';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl } from '../lib/assetUrls';
import { isWcTeam } from '../lib/wcTeams';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const _cache  = {};   // module-level — survives re-renders, cleared on page reload

function authH() { return { Authorization: `Bearer ${getToken()}` }; }


export function TeamIntelTooltip({ teamId, teamName, size = 22, children }) {
  const [intel,   setIntel]   = useState(_cache[teamId] || null);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);

  // Only show tooltip for WC teams
  if (!isWcTeam(teamId)) return <>{children}</>;

  const fetchIntel = async () => {
    if (!teamId || intel || loading) return;
    if (_cache[teamId]) { setIntel(_cache[teamId]); return; }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/football/worldcup/team-intel/${teamId}`,
        { headers: authH() }
      );
      _cache[teamId] = res.data;
      setIntel(res.data);
    } catch {
      _cache[teamId] = null;
    } finally {
      setLoading(false);
    }
  };

  const rankDiff = intel ? (intel.previous_rank - intel.rank) : 0;

  const body = (
    <div className="min-w-[200px] space-y-2.5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <img src={badgeUrl(teamId)} alt={teamName || ''}
          style={{ width: 28, height: 28 }} className="object-contain flex-shrink-0" />
        <div>
          <p className="font-bold text-sm leading-tight">{intel?.pa_team_name || teamName}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{intel?.confederation || ''}</p>
        </div>
      </div>

      {loading && <p className="text-xs text-muted-foreground">Loading…</p>}

      {intel && (
        <>
          {/* FIFA Rank */}
          <div className="flex items-center justify-between border-t border-border/50 pt-2">
            <span className="text-xs text-muted-foreground">FIFA Ranking</span>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-black leading-none">#{intel.rank}</span>
              {rankDiff !== 0 && (
                <span className={`text-[10px] font-bold ${rankDiff > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {rankDiff > 0 ? `▲${rankDiff}` : `▼${Math.abs(rankDiff)}`}
                </span>
              )}
            </div>
          </div>

          {/* Recent form */}
          {intel.recent_matches?.length > 0 && (
            <div className="border-t border-border/50 pt-2 space-y-1">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">
                Recent Results
              </p>
              {intel.recent_matches.slice(0, 5).map((m, i) => {
                const isHome    = m.home_code === intel.country_code;
                const teamScore = isHome ? m.home_score : m.away_score;
                const oppScore  = isHome ? m.away_score : m.home_score;
                const oppName   = isHome ? m.away : m.home;
                const oppCode   = isHome ? m.away_code : m.home_code;
                const result    = teamScore > oppScore ? 'W' : teamScore < oppScore ? 'L' : 'D';
                const col       = result === 'W' ? 'text-green-400' : result === 'L' ? 'text-red-400' : 'text-amber-400';
                return (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-1 min-w-0">
                      <span className={`font-black w-4 flex-shrink-0 ${col}`}>{result}</span>
                      <span className="text-muted-foreground truncate">{oppName}</span>
                    </div>
                    <span className="font-bold tabular-nums flex-shrink-0">{teamScore}–{oppScore}</span>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-[9px] font-semibold text-muted-foreground/60 uppercase tracking-widest border-t border-border/30 pt-1.5">
            Pre-tournament data
          </p>
        </>
      )}

      {!loading && !intel && (
        <p className="text-xs text-muted-foreground border-t border-border/50 pt-2">No data available</p>
      )}
    </div>
  );

  const handleOpen = () => { fetchIntel(); setOpen(true); };

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        asChild
        onMouseEnter={handleOpen}
        onFocus={handleOpen}
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : handleOpen(); }}
      >
        <span className="cursor-pointer">{children}</span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="bg-card text-foreground border border-border shadow-xl text-xs p-3 rounded-xl"
      >
        {body}
      </TooltipContent>
    </Tooltip>
  );
}
